// src/whatsappChat/components/ThreadView.jsx
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useWaThread } from '../api/hooks';
import { normalizeError } from '../api/client';
import { dayKey, formatDay, formatPhone, initials } from '../api/content';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import { showToast } from '../../components/toast.jsx';

const NEAR_BOTTOM_PX = 120;

export function ThreadView({ conversationId, listRow, numbers, numbersLoaded, canSend, onBack }) {
  const { thread, isLoading, isError, error, loadOlder, loadingOlder, live, fetchNew } =
    useWaThread(conversationId);
  const scrollRef = useRef(null);
  // What the next layout pass should do with the scroll position.
  const pendingScroll = useRef({ mode: 'bottom', prevHeight: 0 });

  const messages = thread?.messages;
  const conversation = thread?.conversation;
  const phone = conversation?.phone || listRow?.phone;
  const numberId = conversation?.number_id || listRow?.number_id;
  const numberName = numbers.find((n) => n.number_id === numberId)?.name || listRow?.number_name || null;
  const subscriber = thread?.subscriber;
  const name = subscriber?.fullname || listRow?.contact_name || null;

  useEffect(() => {
    pendingScroll.current = { mode: 'bottom', prevHeight: 0 };
  }, [conversationId]);

  // The server withdrew access to this chat (chat:closed).
  const accessLost = Boolean(thread?.accessLost);
  useEffect(() => {
    if (!accessLost) return;
    showToast.warning('You no longer have access to this chat.');
    onBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLost]);

  const firstId = messages?.[0]?.id;
  const lastId = messages?.length ? messages[messages.length - 1].id : undefined;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !messages) return;
    const { mode, prevHeight } = pendingScroll.current;
    if (mode === 'bottom') {
      el.scrollTop = el.scrollHeight;
    } else if (mode === 'keep') {
      el.scrollTop += el.scrollHeight - prevHeight;
    }
    pendingScroll.current = { mode: 'none', prevHeight: 0 };
    // firstId/lastId change exactly when rows are prepended or appended
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstId, lastId, conversationId]);

  // Before new rows render, decide whether to follow them to the bottom.
  const lastSeenId = useRef(undefined);
  if (lastId !== lastSeenId.current) {
    const el = scrollRef.current;
    if (el && lastSeenId.current !== undefined && pendingScroll.current.mode === 'none') {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      if (nearBottom) pendingScroll.current = { mode: 'bottom', prevHeight: 0 };
    }
    lastSeenId.current = lastId;
  }

  // The sent message comes back through the socket; without it, fetch it once.
  const handleSent = () => {
    pendingScroll.current = { mode: 'bottom', prevHeight: 0 };
    if (!live) fetchNew();
  };

  const handleLoadOlder = () => {
    const el = scrollRef.current;
    pendingScroll.current = { mode: 'keep', prevHeight: el ? el.scrollHeight : 0 };
    loadOlder().catch(() => {
      pendingScroll.current = { mode: 'none', prevHeight: 0 };
    });
  };

  let body;
  if (isLoading) {
    body = <div className="wa-state">Loading messages…</div>;
  } else if (isError) {
    body = <div className="wa-state">{normalizeError(error).message}</div>;
  } else if (!messages || messages.length === 0) {
    body = <div className="wa-state">No messages in this chat yet.</div>;
  } else {
    let lastDay = '';
    body = (
      <>
        {thread.hasMoreOlder && (
          <div className="wa-thread-older">
            <button
              type="button"
              className="wa-btn wa-btn-secondary"
              onClick={handleLoadOlder}
              disabled={loadingOlder}
            >
              {loadingOlder ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        )}
        {messages.map((message) => {
          const day = dayKey(message.message_at);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={message.id}>
              {showDay && (
                <div className="wa-day">
                  <span>{formatDay(message.message_at)}</span>
                </div>
              )}
              <MessageBubble message={message} />
            </React.Fragment>
          );
        })}
      </>
    );
  }

  return (
    <section className="wa-thread">
      <header className="wa-thread-head">
        <button type="button" className="wa-icon-btn wa-thread-back" onClick={onBack} aria-label="Back to chats">
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <span className={`wa-avatar${subscriber || listRow?.subscriber_id ? '' : ' is-unknown'}`}>
          {initials(name, phone)}
        </span>
        <div className="wa-thread-who">
          <div className="wa-thread-name">{name || formatPhone(phone) || 'Chat'}</div>
          <div className="wa-thread-sub">
            {name && phone && <span>{formatPhone(phone)}</span>}
            {numberName ? (
              <span className="wa-tag wa-tag-number">
                <FontAwesomeIcon icon={faWhatsapp} /> {numberName}
              </span>
            ) : (
              thread && <span className="wa-tag wa-tag-danger">Number removed</span>
            )}
          </div>
        </div>
        {subscriber?.id ? (
          <Link className="wa-btn wa-btn-secondary" to={`/Subscriber/Edit/${subscriber.id}`}>
            <FontAwesomeIcon icon={faUser} /> <span className="wa-hide-sm">Subscriber</span>
          </Link>
        ) : (
          thread && <span className="wa-tag">Unknown number</span>
        )}
      </header>

      <div className="wa-thread-body" ref={scrollRef}>
        {body}
      </div>

      {thread &&
        (canSend ? (
          <Composer
            conversationId={Number(conversationId)}
            numberId={numberId}
            numberConfigured={!numbersLoaded || Boolean(numberName)}
            windowOpen={thread.windowOpen}
            onSent={handleSent}
          />
        ) : (
          <footer className={`wa-thread-foot${thread.windowOpen ? '' : ' is-closed'}`}>
            <FontAwesomeIcon icon={faClock} />
            {thread.windowOpen ? '24-hour window is open.' : '24-hour window is closed.'}
          </footer>
        ))}
    </section>
  );
}
